import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { api } from '../../App';
import { toast } from 'sonner';
import { formatAddress } from '../../utils/format';
import { 
  Edit, Building2, Home, Car, MapPin, Eye, EyeOff, Camera, User, Plus, Settings, Trash2, Gift
} from 'lucide-react';
import { formatDuration } from '../../utils/format';

// Analytics components
import { TimeRangeSelector } from './analytics/TimeRangeSelector';
import { SummaryCards } from './analytics/SummaryCards';
import { BookingTrendChart, RevenueChart } from './analytics/TrendCharts';
import { ClientRetention } from './analytics/ClientRetention';
import { PopularTimes } from './analytics/PopularTimes';
import { ServiceRanking } from './analytics/ServiceRanking';
import { MissedProspectsWidget } from './MissedProspectsWidget';
import { SpecialtyPicker } from '../onboarding/SpecialtyPicker';
import { SpecialtyBadges } from '../SpecialtyBadges';

const LIST_LIMIT = 10;

const SERVICE_TYPE_OPTIONS = [
  { id: 'salon', label: 'Salon / Studio', icon: Building2, desc: 'Clients visit your salon or studio' },
  { id: 'home', label: 'Home-based', icon: Home, desc: 'Clients visit your home' },
  { id: 'mobile', label: 'Mobile', icon: Car, desc: 'You travel to the client' },
];

const RADIUS_OPTIONS = [1, 3, 5, 10, 15, 20, 25, 30];

export function OverviewTab({
  provider, services, bookings, 
  editingProfile, profileForm, setProfileForm, setEditingProfile,
  handleProfileUpdate, handleProfileImageUpload, uploadingProfileImg,
  handleBookingStatus, setActiveTab, setProvider
}) {
  const [showAllPending, setShowAllPending] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [activePeriod, setActivePeriod] = useState('30d');

  // Service types editing state
  const [editingServiceTypes, setEditingServiceTypes] = useState(false);
  const [stForm, setStForm] = useState({ service_types: [], salon_address: '', home_address: '', service_radius: '', free_travel: false, travel_charges: [] });
  const [savingServiceTypes, setSavingServiceTypes] = useState(false);

  const fetchAnalytics = useCallback(async (period, startDate, endDate) => {
    setAnalyticsLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.set('start_date', startDate);
        params.set('end_date', endDate);
      } else {
        params.set('period', period);
      }
      const res = await api.get(`/providers/me/analytics?${params.toString()}`);
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAnalytics(activePeriod);
  }, [fetchAnalytics, activePeriod]);

  const handlePeriodChange = (period) => {
    setActivePeriod(period);
    fetchAnalytics(period);
  };

  const handleCustomRange = (start, end) => {
    setActivePeriod('custom');
    fetchAnalytics(null, start, end);
  };

  const openServiceTypesEditor = () => {
    setStForm({
      service_types: [...(provider.service_types || [])],
      salon_address: formatAddress(provider.salon_address) || '',
      home_address: formatAddress(provider.home_address) || '',
      service_radius: provider.service_radius || '',
      free_travel: provider.free_travel || false,
      travel_charges: provider.travel_charges?.length ? [...provider.travel_charges] : [],
    });
    setEditingServiceTypes(true);
  };

  const toggleServiceType = (typeId) => {
    setStForm(prev => ({
      ...prev,
      service_types: prev.service_types.includes(typeId)
        ? prev.service_types.filter(t => t !== typeId)
        : [...prev.service_types, typeId]
    }));
  };

  const handleSaveServiceTypes = async () => {
    if (stForm.service_types.length === 0) { toast.error('Please select at least one service type'); return; }
    if (stForm.service_types.includes('salon') && !stForm.salon_address) { toast.error('Please provide your salon/studio address'); return; }
    if (stForm.service_types.includes('home') && !stForm.home_address) { toast.error('Please provide your home address'); return; }
    if (stForm.service_types.includes('mobile') && !stForm.service_radius) { toast.error('Please select your travel radius'); return; }

    // Validate travel charges if not free travel
    if (stForm.service_types.includes('mobile') && !stForm.free_travel && stForm.travel_charges.length > 0) {
      for (const tier of stForm.travel_charges) {
        if (!tier.max_miles || tier.max_miles <= 0 || !tier.charge || tier.charge <= 0) {
          toast.error('Please fill in all travel charge tiers or remove empty ones');
          return;
        }
      }
    }

    setSavingServiceTypes(true);
    try {
      const res = await api.put('/providers/me', {
        service_types: stForm.service_types,
        salon_address: stForm.salon_address || null,
        home_address: stForm.home_address || null,
        service_radius: stForm.service_radius ? Number(stForm.service_radius) : null,
        free_travel: stForm.service_types.includes('mobile') ? stForm.free_travel : false,
        travel_charges: stForm.service_types.includes('mobile') && !stForm.free_travel ? stForm.travel_charges : [],
      });
      setProvider(res.data);
      setEditingServiceTypes(false);
      toast.success('Service types updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setSavingServiceTypes(false); }
  };

  const sortedTravelCharges = useMemo(() =>
    [...(provider.travel_charges || [])].sort((a, b) => a.max_miles - b.max_miles),
    [provider.travel_charges]
  );

  const pendingBookings = useMemo(() =>
    (bookings || []).filter(b => b.status === 'pending'),
    [bookings]
  );

  function renderTravelInfo() {
    if (provider.free_travel) {
      return (
        <div className="flex items-center gap-1 mt-1">
          <Gift className="h-3 w-3 text-green-600" />
          <span className="text-xs text-green-600 font-medium">Free travel</span>
        </div>
      );
    }
    if (sortedTravelCharges.length > 0) {
      return (
        <div className="mt-1 space-y-0.5">
          {sortedTravelCharges.map((tier) => (
            <p key={`display-tier-${tier.max_miles}`} className="text-xs text-stone-400">
              Up to {tier.max_miles} {tier.max_miles === 1 ? 'mile' : 'miles'}, £{Number(tier.charge).toFixed(2)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  }

  return (
    <>
      {/* Missed-prospects nudge, renders only for free-tier providers
          with ≥1 unread prospect message. Pro returns count=0, widget hides. */}
      <MissedProspectsWidget />

      {/* Profile Section */}
      <div className="card-surface p-4 sm:p-6 mb-6" data-testid="profile-section">
        <div className="flex items-start gap-4 sm:gap-6">
          <div className="relative group flex-shrink-0" data-testid="profile-picture">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-[#F9F6F0] flex items-center justify-center">
              {provider.profile_image ? (
                <img src={provider.profile_image} alt={provider.brand_name} className="w-full h-full object-cover" />
              ) : (
                <User className="h-8 w-8 text-[#D4AF37]" />
              )}
            </div>
            <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
              <Camera className="h-5 w-5 text-white" />
              <input type="file" accept="image/*" className="hidden" onChange={handleProfileImageUpload} disabled={uploadingProfileImg} data-testid="profile-image-upload" />
            </label>
            {uploadingProfileImg && <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-full"><div className="animate-spin rounded-full h-5 w-5 border-t-2 border-[#D4AF37]" /></div>}
          </div>
          <div className="flex-1 min-w-0">
            {editingProfile ? (
              <div className="space-y-3">
                <Input value={profileForm.brand_name} onChange={e => setProfileForm({...profileForm, brand_name: e.target.value})} placeholder="Business name" className="input-zuriya" data-testid="edit-brand-name" />
                <textarea value={profileForm.bio} onChange={e => setProfileForm({...profileForm, bio: e.target.value})} placeholder="Your bio..." className="w-full p-3 border border-[#E6E4DD] rounded-lg text-sm resize-none h-20 focus:ring-1 focus:ring-[#D4AF37] outline-none" data-testid="edit-bio" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">Area</label>
                    <Input value={profileForm.location} onChange={e => setProfileForm({...profileForm, location: e.target.value})} placeholder="e.g. Brixton" className="input-zuriya" data-testid="edit-location" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">Postcode</label>
                    <Input value={profileForm.postcode} onChange={e => setProfileForm({...profileForm, postcode: e.target.value.toUpperCase()})} placeholder="e.g. SW9 8LQ" className="input-zuriya font-mono uppercase" maxLength={10} data-testid="edit-postcode" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-stone-500 mb-2 block">Specialties</label>
                  <SpecialtyPicker
                    categories={provider.categories || []}
                    selected={profileForm.specialties || []}
                    onChange={(specialties) => setProfileForm({ ...profileForm, specialties })}
                    testId="edit-specialty-picker"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleProfileUpdate} className="btn-primary text-sm" data-testid="save-profile-btn">Save</Button>
                  <Button variant="outline" onClick={() => setEditingProfile(false)} className="text-sm">Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-xl font-medium text-[#1A1A1A]">{provider.brand_name}</h2>
                <p className="text-sm text-stone-500 mt-1 line-clamp-2">{provider.bio}</p>
                {(provider.location || provider.postcode) && (
                  <p className="text-xs text-stone-400 mt-1">
                    {provider.location}{provider.postcode ? ` · ${provider.postcode}` : ''}
                  </p>
                )}
                <SpecialtyBadges ids={provider.specialties} testId="overview-specialties" />
                <button onClick={() => { setProfileForm({ bio: provider.bio || '', brand_name: provider.brand_name || '', location: provider.location || '', postcode: provider.postcode || '', specialties: provider.specialties || [] }); setEditingProfile(true); }} className="text-xs text-[#D4AF37] mt-2 hover:underline flex items-center gap-1" data-testid="edit-profile-btn">
                  <Edit className="h-3 w-3" /> Edit profile
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Analytics Section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <h3 className="font-heading text-xl font-medium text-[#1A1A1A]" data-testid="analytics-heading">Performance</h3>
          <TimeRangeSelector
            activePeriod={activePeriod}
            onPeriodChange={handlePeriodChange}
            onCustomRange={handleCustomRange}
          />
        </div>

        {analyticsLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]" />
          </div>
        ) : analytics ? (
          <div className="space-y-5">
            <SummaryCards data={analytics} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <BookingTrendChart data={analytics.booking_trend} granularity={analytics.granularity} />
              <RevenueChart data={analytics.booking_trend} granularity={analytics.granularity} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <ClientRetention data={analytics} />
              <PopularTimes popularHours={analytics.popular_hours} popularDays={analytics.popular_days} />
              <ServiceRanking services={analytics.service_ranking} />
            </div>
          </div>
        ) : (
          <div className="card-surface p-8 text-center">
            <p className="text-stone-500">Analytics data unavailable.</p>
          </div>
        )}
      </div>

      {/* Service Locations & Types */}
      <div className="card-surface p-4 sm:p-6 mb-6" data-testid="location-settings">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-heading text-xl font-medium text-[#1A1A1A] flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#D4AF37]" />
            Your Service Locations
          </h3>
          {!editingServiceTypes && (
            <button onClick={openServiceTypesEditor} className="text-xs text-[#D4AF37] hover:underline flex items-center gap-1" data-testid="edit-service-types-btn">
              <Settings className="h-3 w-3" /> Manage
            </button>
          )}
        </div>

        {editingServiceTypes ? (
          <div className="space-y-4" data-testid="service-types-editor">
            <p className="text-sm text-stone-500">Select where you offer your services.</p>
            <div className="space-y-3">
              {SERVICE_TYPE_OPTIONS.map((type) => {
                const isActive = stForm.service_types.includes(type.id);
                const Icon = type.icon;
                return (
                  <div key={type.id}>
                    <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${isActive ? 'border-[#D4AF37] bg-[#F9F6F0]' : 'border-[#E6E4DD] hover:border-[#D4AF37]'}`}>
                      <Checkbox checked={isActive} onCheckedChange={() => toggleServiceType(type.id)} data-testid={`st-checkbox-${type.id}`} />
                      <Icon className="h-5 w-5 text-[#D4AF37]" />
                      <div>
                        <p className="text-sm font-medium text-[#1A1A1A]">{type.label}</p>
                        <p className="text-xs text-stone-400">{type.desc}</p>
                      </div>
                    </label>
                    {isActive && type.id === 'salon' && (
                      <div className="ml-11 mt-2">
                        <Input value={stForm.salon_address} onChange={e => setStForm(prev => ({ ...prev, salon_address: e.target.value }))} placeholder="Salon/studio address" className="input-zuriya text-sm" data-testid="st-salon-address" />
                      </div>
                    )}
                    {isActive && type.id === 'home' && (
                      <div className="ml-11 mt-2">
                        <Input value={stForm.home_address} onChange={e => setStForm(prev => ({ ...prev, home_address: e.target.value }))} placeholder="Home address" className="input-zuriya text-sm" data-testid="st-home-address" />
                      </div>
                    )}
                    {isActive && type.id === 'mobile' && (
                      <div className="ml-11 mt-2 space-y-3">
                        <Select value={stForm.service_radius ? String(stForm.service_radius) : ''} onValueChange={v => setStForm(prev => ({ ...prev, service_radius: Number(v) }))}>
                          <SelectTrigger className="input-zuriya text-sm" data-testid="st-mobile-radius"><SelectValue placeholder="Select travel radius" /></SelectTrigger>
                          <SelectContent>
                            {RADIUS_OPTIONS.map(r => (<SelectItem key={r} value={String(r)}>{r} {r === 1 ? 'mile' : 'miles'}</SelectItem>))}
                          </SelectContent>
                        </Select>

                        {/* Free travel toggle */}
                        <label className="flex items-center gap-3 p-3 rounded-lg border border-[#E6E4DD] cursor-pointer hover:border-green-400 transition-colors" data-testid="free-travel-toggle">
                          <Checkbox
                            checked={stForm.free_travel}
                            onCheckedChange={(checked) => setStForm(prev => ({ ...prev, free_travel: !!checked }))}
                          />
                          <Gift className="h-4 w-4 text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-[#1A1A1A]">Free travel</p>
                            <p className="text-xs text-stone-400">No travel charge for clients</p>
                          </div>
                        </label>

                        {/* Travel charge tiers */}
                        {!stForm.free_travel && (
                          <div className="space-y-2" data-testid="travel-charges-section">
                            <p className="text-xs text-stone-500 font-medium">Travel charges by distance</p>
                            {stForm.travel_charges.map((tier, idx) => (
                              <div key={`tier-${idx}`} className="flex flex-wrap items-center gap-2">
                                <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0">
                                  <span className="text-xs text-stone-500 whitespace-nowrap">Up to</span>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={tier.max_miles || ''}
                                    onChange={e => {
                                      const val = parseInt(e.target.value) || 0;
                                      setStForm(prev => {
                                        const updated = [...prev.travel_charges];
                                        updated[idx] = { ...updated[idx], max_miles: val };
                                        return { ...prev, travel_charges: updated };
                                      });
                                    }}
                                    className="input-zuriya text-sm w-16"
                                    placeholder="mi"
                                    data-testid={`tier-miles-${idx}`}
                                  />
                                  <span className="text-xs text-stone-500">miles</span>
                                  <div className="flex items-center gap-0.5 ml-1">
                                    <span className="text-sm text-stone-600">£</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.50"
                                      value={tier.charge || ''}
                                      onChange={e => {
                                        const val = parseFloat(e.target.value) || 0;
                                        setStForm(prev => {
                                          const updated = [...prev.travel_charges];
                                          updated[idx] = { ...updated[idx], charge: val };
                                          return { ...prev, travel_charges: updated };
                                        });
                                      }}
                                      className="input-zuriya text-sm w-20"
                                      placeholder="0.00"
                                      data-testid={`tier-charge-${idx}`}
                                    />
                                  </div>
                                </div>
                                <button
                                  onClick={() => setStForm(prev => ({ ...prev, travel_charges: prev.travel_charges.filter((_, i) => i !== idx) }))}
                                  className="p-1 text-red-400 hover:text-red-600 transition-colors shrink-0"
                                  data-testid={`tier-remove-${idx}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => setStForm(prev => ({ ...prev, travel_charges: [...prev.travel_charges, { max_miles: '', charge: '' }] }))}
                              className="text-xs text-[#D4AF37] hover:underline flex items-center gap-1"
                              data-testid="add-travel-tier-btn"
                            >
                              <Plus className="h-3 w-3" /> Add distance tier
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSaveServiceTypes} className="btn-primary text-sm" disabled={savingServiceTypes} data-testid="save-service-types-btn">{savingServiceTypes ? 'Saving...' : 'Save Changes'}</Button>
              <Button variant="outline" onClick={() => setEditingServiceTypes(false)} className="text-sm">Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {provider.service_types?.includes('salon') && (
              <div className="flex items-start gap-3 p-3 bg-[#F9F6F0] rounded-lg">
                <Building2 className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <div><p className="font-medium text-[#1A1A1A] text-sm">Salon / Studio</p><p className="text-stone-500 text-sm">{formatAddress(provider.salon_address) || 'No address set'}</p></div>
              </div>
            )}
            {provider.service_types?.includes('home') && (
              <div className="p-3 bg-[#F9F6F0] rounded-lg space-y-2">
                <div className="flex items-start gap-3">
                  <Home className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <div className="flex-1"><p className="font-medium text-[#1A1A1A] text-sm">Home-based</p><p className="text-stone-500 text-sm">{formatAddress(provider.home_address) || 'No address set'}</p></div>
                </div>
                <div className="flex items-center justify-between ml-8 pt-2 border-t border-[#E6E4DD]">
                  <div className="flex items-center gap-2">
                    {provider.show_full_home_address ? <Eye className="h-4 w-4 text-[#D4AF37]" /> : <EyeOff className="h-4 w-4 text-stone-400" />}
                    <span className="text-sm text-stone-600">{provider.show_full_home_address ? 'Full address visible to clients' : 'Only area shown before booking'}</span>
                  </div>
                </div>
              </div>
            )}
            {provider.service_types?.includes('mobile') && (
              <div className="flex items-start gap-3 p-3 bg-[#F9F6F0] rounded-lg">
                <Car className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-[#1A1A1A] text-sm">Mobile</p>
                  <p className="text-stone-500 text-sm">{provider.service_radius ? `Travels up to ${provider.service_radius} ${provider.service_radius === 1 ? 'mile' : 'miles'}` : 'No radius set'}</p>
                  {renderTravelInfo()}
                </div>
              </div>
            )}
            {(!provider.service_types || provider.service_types.length === 0) && (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm mb-3">No service types configured yet</p>
                <Button onClick={openServiceTypesEditor} className="btn-primary text-sm" data-testid="add-service-types-btn"><Plus className="h-4 w-4 mr-1" /> Add Service Types</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pending Bookings */}
      {pendingBookings.length > 0 && (
        <div className="card-surface p-4 sm:p-6">
          <h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-4">
            Pending Bookings
            {pendingBookings.length > LIST_LIMIT && (
              <span className="text-sm font-normal text-stone-400 ml-2">({pendingBookings.length})</span>
            )}
          </h3>
          <div className="space-y-4" data-testid="pending-bookings-list">
            {(showAllPending ? pendingBookings : pendingBookings.slice(0, LIST_LIMIT)).map((booking) => (
              <div key={booking.booking_id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-[#F9F6F0] rounded-lg gap-3">
                <div>
                  <p className="font-medium text-[#1A1A1A]">{booking.service_name}</p>
                  <p className="text-sm text-stone-500">{booking.client_name} · {booking.date} at {booking.time}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="btn-primary" onClick={() => handleBookingStatus(booking.booking_id, 'confirmed')} data-testid={`confirm-booking-${booking.booking_id}`}>Confirm</Button>
                  <Button size="sm" variant="outline" onClick={() => handleBookingStatus(booking.booking_id, 'cancelled')}>Decline</Button>
                </div>
              </div>
            ))}
          </div>
          {pendingBookings.length > LIST_LIMIT && (
            <div className="mt-4 text-center">
              <Button variant="outline" onClick={() => setShowAllPending(!showAllPending)} className="border-[#E6E4DD] text-stone-600" data-testid="pending-view-all-btn">
                {showAllPending ? 'Show Less' : `View All ${pendingBookings.length} Bookings`}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
